// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Turning a burst of GNSS fixes taken from one spot into a single better fix.
//
// This is the only accuracy win available to us in software. We cannot ask
// Android for SBAS/EGNOS or for L5 — both are chipset-level and have no public
// API (see ./gnssQuality for the observability side of that). What we CAN do is
// exploit the one thing a golf app has that a navigation app does not: the
// player stands still for several seconds before every shot, so consecutive
// fixes sample the same physical point and their independent error averages
// down.
//
// Pure module, no platform imports: both the hole view (live distance readout)
// and shot marking feed it, and every rule below is unit-tested directly.

import { distanceMeters } from './geo';

/** A fix as this module needs it. Shaped to match both expo-location's
 *  `coords` + `timestamp` and shot tracking's own recent-fix buffer. */
export type ClusterFix = {
  lat: number;
  lon: number;
  /** Reported horizontal accuracy in metres — a 68% confidence radius on
   *  Android, treated here as one sigma. Null when the platform withholds it. */
  accuracy: number | null;
  /** Platform ground speed in m/s, or null when not reported. */
  speed: number | null;
  /** Device clock in ms. */
  ts: number;
};

export type ClusterFit = {
  lat: number;
  lon: number;
  /** Standard error of the combined position, or null when no input carried an
   *  accuracy and there is therefore nothing honest to report. */
  accuracy: number | null;
  /** How many fixes survived outlier rejection and went into the average. */
  sampleCount: number;
};

/** Below this the player is standing, not walking. Same figure the geodata
 *  service uses for dwell detection. */
export const STATIONARY_SPEED_MPS = 0.7;

/** A fix older than this says nothing about whether the player moves NOW. */
export const DEFAULT_FRESHNESS_MS = 9000;

/** Sigma assumed for a fix whose platform reported no accuracy: enough to keep
 *  it in the average, little enough that it never outvotes a measured fix. */
const NOMINAL_ACCURACY_M = 12;

/** A fix further than this many sigmas from the cluster median is multipath or
 *  a provider switch, not noise — averaging it in would drag the result. */
const OUTLIER_SIGMAS = 3;

/** Floor under the rejection radius: a very tight cluster must not start
 *  rejecting its own members over sub-metre disagreement. */
const MIN_OUTLIER_RADIUS_M = 3;

/**
 * Floor under the improvement, as a fraction of the sharpest single fix.
 *
 * Inverse-variance weighting predicts sigma/sqrt(N), which assumes independent
 * errors. Over a few seconds GNSS error is anything but: multipath off the same
 * trees and ionospheric bias are common to every fix in the burst and do not
 * average out at all. Halving the best single fix is the defensible claim; the
 * unhedged sqrt(N) would have us printing "±0.6 m" on a 4 m device.
 */
const CORRELATED_ERROR_FLOOR = 0.5;

/** Great-circle distance in metres — the shared implementation in ./geo,
 *  kept under this module's historical export name. */
export const haversineMeters = distanceMeters;

/**
 * Whether the newest fix says the player is standing still: it must be fresh,
 * and slow. Speed comes from the platform when it reports one, and from
 * displacement over the last two fixes when it does not.
 */
export const isStationary = (
  fixes: readonly ClusterFix[],
  now: number,
  freshnessMs: number = DEFAULT_FRESHNESS_MS
): boolean => {
  const latest = fixes[fixes.length - 1];
  if (!latest || now - latest.ts > freshnessMs) return false;

  if (latest.speed !== null && Number.isFinite(latest.speed)) {
    return latest.speed < STATIONARY_SPEED_MPS;
  }

  const previous = fixes[fixes.length - 2];
  if (!previous) return false;
  const dtSeconds = (latest.ts - previous.ts) / 1000;
  if (dtSeconds <= 0) return false;
  return haversineMeters(previous, latest) / dtSeconds < STATIONARY_SPEED_MPS;
};

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const sigmaOf = (fix: ClusterFix): number =>
  fix.accuracy !== null && Number.isFinite(fix.accuracy) && fix.accuracy > 0
    ? fix.accuracy
    : NOMINAL_ACCURACY_M;

/**
 * Combine fixes believed to sample the same spot into one position.
 *
 * Coordinate-wise median first to find a robust centre, outliers rejected
 * against it, then an inverse-variance-weighted mean of the survivors. The
 * median is only the reference point, never the answer — with 3-4 samples it
 * quantises the result to the input positions, which is exactly the jitter this
 * exists to remove.
 *
 * Callers are responsible for only passing fixes taken while stationary; this
 * function has no way to tell a standing cluster from a walked line.
 *
 * @param maxAccuracyMeters drop fixes reporting worse than this before combining.
 */
export const combineFixes = (
  fixes: readonly ClusterFix[],
  maxAccuracyMeters = Number.POSITIVE_INFINITY
): ClusterFit | null => {
  const usable = fixes.filter(
    (fix) =>
      Number.isFinite(fix.lat) &&
      Number.isFinite(fix.lon) &&
      // A null accuracy is kept (it gets the nominal sigma); a reported one is
      // held to the caller's ceiling. Zero or negative means "no idea", not
      // "perfect" — Android uses it that way and it would take infinite weight.
      (fix.accuracy === null ||
        !Number.isFinite(fix.accuracy) ||
        (fix.accuracy > 0 && fix.accuracy <= maxAccuracyMeters))
  );

  if (usable.length === 0) return null;

  const anyReportedAccuracy = usable.some(
    (fix) => fix.accuracy !== null && Number.isFinite(fix.accuracy) && fix.accuracy > 0
  );

  if (usable.length === 1) {
    const only = usable[0];
    return {
      lat: only.lat,
      lon: only.lon,
      accuracy: anyReportedAccuracy ? only.accuracy : null,
      sampleCount: 1,
    };
  }

  const centre = {
    lat: median(usable.map((fix) => fix.lat)),
    lon: median(usable.map((fix) => fix.lon)),
  };

  const kept = usable.filter((fix) => {
    const radius = Math.max(OUTLIER_SIGMAS * sigmaOf(fix), MIN_OUTLIER_RADIUS_M);
    return haversineMeters(centre, fix) <= radius;
  });
  // Every fix disagreeing with the median by more than its own error bar means
  // the burst is not one cluster; the median itself is then the best guess.
  const survivors = kept.length > 0 ? kept : usable;

  let weightSum = 0;
  let latSum = 0;
  let lonSum = 0;
  let sharpestSigma = Number.POSITIVE_INFINITY;

  for (const fix of survivors) {
    const sigma = sigmaOf(fix);
    const weight = 1 / (sigma * sigma);
    weightSum += weight;
    latSum += weight * fix.lat;
    lonSum += weight * fix.lon;
    if (sigma < sharpestSigma) sharpestSigma = sigma;
  }

  const standardError = Math.sqrt(1 / weightSum);

  return {
    lat: latSum / weightSum,
    lon: lonSum / weightSum,
    accuracy: anyReportedAccuracy
      ? Math.max(standardError, sharpestSigma * CORRELATED_ERROR_FLOOR)
      : null,
    sampleCount: survivors.length,
  };
};

export const __testOnly = {
  NOMINAL_ACCURACY_M,
  OUTLIER_SIGMAS,
  MIN_OUTLIER_RADIUS_M,
  CORRELATED_ERROR_FLOOR,
};
